import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "context/AuthContext";
import { useStore } from "context/StoreContext";
import { Link, Navigate } from "react-router-dom";
import { useGoogleReCaptcha } from "@google-recaptcha/react";
import { getLogoUrl } from "utils/logoUtils";
import { PasswordInput } from "components/ui";

const Login = () => {
 const { t } = useTranslation();
 const [usernameOrEmail, setUsernameOrEmail] = useState<string>("");
 const [password, setPassword] = useState("");
 const [error, setError] = useState("");
 const [isLoading, setIsLoading] = useState(false);
 const { store } = useStore();
 const { login, userInfo: user, user: userFromToken } = useAuth();
 const googleReCaptcha = useGoogleReCaptcha();

 const handleLogin = async (e: React.FormEvent) => {
  e.preventDefault();
  setError("");
  setIsLoading(true);

  // Basic client-side validation
  if (!usernameOrEmail.trim()) {
   setError("Please enter your username or email address");
   setIsLoading(false);
   return;
  }

  if (!password) {
   setError("Please enter your password");
   setIsLoading(false);
   return;
  }

  try {
   // In test mode (Cypress), bypass reCAPTCHA
   const isTestMode = typeof window !== "undefined" && (window as any).Cypress;
   let captchaValue = "";
   
   if (isTestMode) {
    // Use test reCAPTCHA token for Cypress tests
    // Google reCAPTCHA test tokens: https://developers.google.com/recaptcha/docs/faq#id-like-to-run-automated-tests-with-recaptcha.-what-should-i-do
    captchaValue = "test-token-cypress";
   } else {
    // Always require reCAPTCHA (except in test mode)
    if (!googleReCaptcha) {
     setError("CAPTCHA is not ready. Please refresh the page and try again.");
     setIsLoading(false);
     return;
    }
    captchaValue = await (googleReCaptcha as any)?.executeV3("action");
    if (!captchaValue) {
     setError("Please complete the CAPTCHA verification");
     setIsLoading(false);
     return;
    }
   }
   await login(usernameOrEmail.trim(), password, captchaValue);
  } catch (err: any) {
   // Improved error handling with better messages
   const errorResponse = err?.response?.data;
   let errorMessage = "An error occurred. Please try again.";

   if (errorResponse?.error) {
    errorMessage = errorResponse.error;
   } else if (errorResponse?.errors && errorResponse.errors.length > 0) {
    errorMessage = errorResponse.errors[0].msg || errorResponse.errors[0].message;
   } else if (errorResponse?.message) {
    errorMessage = errorResponse.message;
   } else if (err?.message) {
    errorMessage = err.message;
   }

   // Don't show technical error messages to users
   if (errorMessage.includes("Network Error") || errorMessage.includes("timeout")) {
    errorMessage = "Unable to connect to the server. Please check your internet connection and try again.";
   }

   setError(errorMessage);
  } finally {
   setIsLoading(false);
  }
 };

 return (
  <div
   className="flex items-center justify-center min-h-screen"
   style={{
    background: "linear-gradient(135deg, #F5F1ED 0%, #EBE5DE 100%)",
   }}
  >
   {(user || userFromToken) && (
     <Navigate
       to={
         (userFromToken?.role === "store_admin" || userFromToken?.role === "admin" || userFromToken?.role === "co_admin")
           ? "/admin/dashboard"
           : "/"
       }
       replace={true}
     />
   )}
   <div className="w-full max-w-md">
    {/* Header */}
    <div className="text-center mb-8">
     {store?.logo && (
      <div className="mb-4 flex justify-center">
       <img
        src={getLogoUrl(store.logo)}
        alt={store.name || "Store logo"}
        className="h-16 w-auto object-contain"
       />
      </div>
     )}
     <h1 className="text-4xl font-bold text-store-primary mb-2 font-store">
      {store?.name || t("common.appName")}
     </h1>
     {!store && (
      <p className="text-store-accent text-sm tracking-widest">
       EMPOWER YOUR COMMERCE
      </p>
     )}
    </div>

    {/* Form Card */}
    <div className="bg-white overflow-hidden">
     <form onSubmit={handleLogin} className="p-8 space-y-6">
      <div>
       <h2 className="text-2xl font-bold text-store-primary mb-1">
        {t("auth.login")}
       </h2>
       <p className="text-store-primary-light text-sm">
        {t("auth.welcomeBack")}
       </p>
      </div>

      {error && (
       <div className="bg-red-50 border border-red-200 p-4">
        <div className="flex items-start">
         <svg
          className="w-5 h-5 text-red-600 mr-2 mt-0.5 flex-shrink-0"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
         >
          <path
           strokeLinecap="round"
           strokeLinejoin="round"
           strokeWidth={2}
           d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
         </svg>
         <p className="text-red-700 text-sm font-medium">{error}</p>
        </div>
       </div>
      )}

      <div>
       <label className="block text-sm font-semibold text-store-primary mb-2">
        {t("common.labels.usernameOrEmail")}
       </label>
       <input
        type="text"
        value={usernameOrEmail}
        onChange={(e) => setUsernameOrEmail(e.target.value)}
        placeholder={t("common.placeholders.enterUsernameOrEmail")}
        autoComplete="username"
        disabled={isLoading}
        className="w-full px-4 py-3 border border-store-secondary focus:outline-none focus:border-store-secondary focus:ring-2 focus:ring-store-secondary focus:ring-opacity-20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
       />
      </div>

      <div>
        <label className="block text-sm font-semibold text-store-primary">
         {t("auth.password")}
        </label>
       <PasswordInput
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder={t("auth.password")}
        autoComplete="current-password"
        disabled={isLoading}
        className="w-full"
       />
       <div className="flex justify-end items-center mt-2">
        <Link
         to="/forgot-password"
         className="text-xs text-store-secondary hover:text-store-secondary-dark transition-colors"
        >
         {t("auth.forgot_password")}
        </Link>
       </div>
      </div>

      <button
       type="submit"
       disabled={isLoading}
       className="w-full bg-store-primary text-white font-semibold py-3 hover:bg-store-secondary transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
      >
       {isLoading ? (
        <>
         <svg
          className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
         >
          <circle
           className="opacity-25"
           cx="12"
           cy="12"
           r="10"
           stroke="currentColor"
           strokeWidth="4"
          ></circle>
          <path
           className="opacity-75"
           fill="currentColor"
           d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
         </svg>
         {t("auth.logging_in")}
        </>
       ) : (
        t("auth.login")
       )}
      </button>
     </form>

     {/* Divider */}
     <div className="px-8 py-4 bg-store-background border-t border-store-secondary">
      <p className="text-center text-sm text-store-primary-light">
       {t("auth.dont_have_account")}{" "}
       <Link
        to="/register"
        className="font-semibold text-store-secondary hover:text-store-secondary-dark transition-colors"
       >
        {t("auth.register_here")}
       </Link>
      </p>
     </div>
    </div>

    {/* Footer Note */}
    <p className="text-center text-xs text-store-primary-light mt-6">
     {t("auth.reCaptchaNote")}
    </p>
   </div>
  </div>
 );
};

export default Login;
